// ============================================================================
// BOQ — range-aware multi-row selection (Finder / Gmail style)
// ============================================================================
// Manages a Set of selected ids with an anchor for shift-range selection.
// Pass the ids in their current display order; call toggle(id, event) from a
// checkbox/row onClick so the native shiftKey is available:
//   • plain click        → toggle that row, move the anchor
//   • shift + click       → apply the row's new on/off state to the whole
//                           contiguous range between the anchor and this row
// toggleAll selects/clears every id currently in display order.
// ============================================================================

import { useCallback, useRef, useState } from 'react';

export function useRangeSelection(orderedIds = [], initialSelected = []) {
  const [selected, setSelectedState] = useState(() => new Set(initialSelected));
  const anchorRef = useRef(null);
  const orderedRef = useRef(orderedIds);
  orderedRef.current = orderedIds;

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id, event) => {
    const ordered = orderedRef.current;
    const anchor = anchorRef.current;
    setSelectedState((prev) => {
      const next = new Set(prev);
      const turnOn = !prev.has(id);
      if (event?.shiftKey && anchor != null && anchor !== id) {
        const a = ordered.indexOf(anchor);
        const b = ordered.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) {
            if (turnOn) next.add(ordered[i]); else next.delete(ordered[i]);
          }
        } else if (turnOn) { next.add(id); } else { next.delete(id); }
      } else if (turnOn) { next.add(id); } else { next.delete(id); }
      return next;
    });
    anchorRef.current = id;
  }, []);

  const toggleAll = useCallback(() => {
    const ordered = orderedRef.current;
    setSelectedState((prev) => {
      const allOn = ordered.length > 0 && ordered.every((id) => prev.has(id));
      return allOn ? new Set() : new Set(ordered);
    });
    anchorRef.current = null;
  }, []);

  const clear = useCallback(() => { setSelectedState(new Set()); anchorRef.current = null; }, []);
  const setSelected = useCallback((ids) => { setSelectedState(new Set(ids)); anchorRef.current = null; }, []);

  return { selected, isSelected, toggle, toggleAll, clear, setSelected };
}
